/**
 * VRS Authentication Service
 *
 * Handles role-based authentication for Video Relay Service.
 * Validates roles with backend server and manages session tokens.
 */
import { ROLE_PERMISSIONS, STORAGE_KEYS, TOKEN_EXPIRY } from './constants';
import { isNativeRuntime } from '../base/whitelabel/functions';
import { logoutNativeSession } from './logoutNativeSession';
import { clearPersistentItems, getPersistentItem, getPersistentItemAsync, hydratePersistentItems, setPersistentItem } from './storage';
/**
 * Get VRS configuration from Jitsi config
 */
function getVRSConfig() {
    const defaults = {
        authEndpoint: 'http://localhost:3003/api/auth',
        jwtSecret: ''
    };
    if (typeof config !== 'undefined' && config.vrs) {
        return { ...defaults, ...config.vrs };
    }
    return defaults;
}
/**
 * Encode an ArrayBuffer to a Base64URL string (no padding).
 */
function toBase64Url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let index = 0; index < bytes.length; index++) {
        binary += String.fromCharCode(bytes[index]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/**
 * Encode a JS object to a Base64URL JSON string.
 */
function encodeBase64UrlJson(payload) {
    const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
    if (!encoder) {
        return btoa(JSON.stringify(payload)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    return toBase64Url(encoder.encode(JSON.stringify(payload)).buffer);
}
/**
 * Create an HMAC-SHA256 signature using the Web Crypto API.
 */
async function hmacSign(data, secret) {
    const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
    const subtle = typeof crypto !== 'undefined' && crypto.subtle ? crypto.subtle : null;
    if (!encoder || !subtle) {
        // Fallback: simple hash-based signature when Web Crypto is unavailable
        let hash = 0;
        const combined = data + secret;
        for (let i = 0; i < combined.length; i++) {
            const char = combined.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash |= 0;
        }
        return btoa(String.fromCharCode(...new Uint8Array(new Int32Array([hash]).buffer)))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    const key = await subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const signature = await subtle.sign('HMAC', key, encoder.encode(data));
    return toBase64Url(signature);
}
class VRSSAuthService {
    constructor() {
        this.currentUser = null;
        this.validationCache = new Map();
        this.cacheTimeout = 60000; // 1 minute cache
        this.config = getVRSConfig();
        if (isNativeRuntime()) {
            // The native root checks secure sign-out recovery before hydrating.
            // Importing the queue service must not race that check or adopt an
            // expired cached user and erase the original recovery credential.
            this.storageReady = Promise.resolve();
        }
        else {
            this.loadUserFromStorage();
            this.storageReady = this.hydrateUserFromStorage();
        }
    }
    ready() {
        return this.storageReady;
    }
    /**
     * Get current user role
     */
    getRole() {
        return this.currentUser?.role || this.getStoredRole();
    }
    /**
     * Get stored role from shared storage (browser storage on web, in-memory on mobile fallback).
     */
    getStoredRole() {
        const role = getPersistentItem(STORAGE_KEYS.USER_ROLE);
        if (role && ['client', 'interpreter', 'admin', 'superadmin'].includes(role)) {
            return role;
        }
        return 'none';
    }
    /**
     * Get current user info
     */
    getUser() {
        return this.currentUser;
    }
    /**
     * Check if user has a specific role
     */
    hasRole(role) {
        const currentRole = this.getRole();
        if (Array.isArray(role)) {
            return role.includes(currentRole);
        }
        return currentRole === role;
    }
    /**
     * Check if user has a specific permission
     */
    hasPermission(permission) {
        const role = this.getRole();
        const permissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.none;
        return permissions[permission] === true;
    }
    /**
     * Check if current session is authenticated
     */
    isAuthenticated() {
        if (!this.currentUser) {
            return false;
        }
        // Check token expiry
        if (this.currentUser.expiresAt && Date.now() > this.currentUser.expiresAt) {
            this.logout();
            return false;
        }
        return this.currentUser.isAuthenticated;
    }
    /**
     * Login as a specific role
     * For clients: creates a temporary session
     * For interpreters: requires server validation
     */
    async login(credentials) {
        await this.storageReady;
        try {
            const { role, name, email, password, languages } = credentials;
            // For clients, we can create a local session
            if (role === 'client') {
                return await this.createClientSession(name);
            }
            // For interpreters/admins, validate with server
            if (role === 'interpreter' || role === 'admin' || role === 'superadmin') {
                if (!email || !password) {
                    return {
                        success: false,
                        error: 'Email and password are required'
                    };
                }
                return this.validateWithServer({
                    role,
                    email,
                    password,
                    name,
                    languages
                });
            }
            return {
                success: false,
                error: 'Invalid role'
            };
        }
        catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: 'Login failed. Please try again.'
            };
        }
    }
    /**
     * Create a client session (no server validation needed for basic access)
     */
    async createClientSession(name) {
        const userId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const now = Date.now();
        const user = {
            id: userId,
            role: 'client',
            name: name || 'Guest',
            isAuthenticated: true,
            authenticatedAt: now,
            expiresAt: now + TOKEN_EXPIRY.CLIENT
        };
        const tokenString = await this.generateLocalToken(user);
        const token = {
            token: tokenString,
            role: 'client',
            userId,
            name: user.name,
            expiresAt: user.expiresAt,
            issuedAt: now
        };
        this.setUser(user, token);
        return {
            success: true,
            token,
            user
        };
    }
    /**
     * Validate credentials with server
     */
    async validateWithServer(credentials) {
        try {
            const response = await fetch(this.getAuthUrl('login'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(credentials)
            });
            const result = await response.json();
            if (!response.ok) {
                return {
                    success: false,
                    error: result.error || 'Authentication failed'
                };
            }
            // Store the user and token
            if (result.token && result.user) {
                const normalizedToken = this.normalizeServerToken(result.token, result.user);
                const normalizedUser = {
                    ...result.user,
                    isAuthenticated: true,
                    authenticatedAt: Date.now(),
                    expiresAt: normalizedToken.expiresAt
                };
                this.setUser(normalizedUser, normalizedToken);
                if (result.refreshToken) {
                    setPersistentItem(STORAGE_KEYS.REFRESH_TOKEN, result.refreshToken);
                }
                result.token = normalizedToken;
                result.user = normalizedUser;
            }
            return result;
        }
        catch (error) {
            console.error('Server validation error:', error);
            return {
                success: false,
                error: 'Unable to connect to authentication server'
            };
        }
    }
    /**
     * Validate current session with server
     */
    async validateSession() {
        await this.storageReady;
        const token = await this.getStoredTokenAsync();
        if (!token) {
            return { valid: false, error: 'No authentication token found' };
        }
        // Check cache
        const cached = this.validationCache.get(token.token);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            return cached.result;
        }
        try {
            const response = await fetch(this.getAuthUrl('validate'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token.token}`
                }
            });
            const result = await response.json();
            const validationResult = response.ok
                ? { valid: true, user: result.user }
                : { valid: false, error: result.error || 'Invalid session' };
            // Cache the result
            this.validationCache.set(token.token, {
                result: validationResult,
                timestamp: Date.now()
            });
            if (!validationResult.valid) {
                this.logout();
            }
            return validationResult;
        }
        catch (error) {
            console.error('Session validation error:', error);
            return { valid: false, error: 'Validation failed' };
        }
    }
    /**
     * Logout and clear session
     */
    logout() {
        if (logoutNativeSession()) {
            this.currentUser = null;
            this.validationCache.clear();
            return;
        }
        const refreshToken = getPersistentItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (refreshToken) {
            // This browser's notification registration, so its pushes stop at sign-out.
            const notificationDeviceId = getPersistentItem('malka_notification_device_id');
            void fetch(this.getAuthUrl('logout'), {
                body: JSON.stringify(notificationDeviceId ? { refreshToken, notificationDeviceId } : { refreshToken }),
                headers: { 'Content-Type': 'application/json' },
                method: 'POST'
            }).catch(() => undefined);
        }
        this.currentUser = null;
        clearPersistentItems([
            STORAGE_KEYS.USER_ROLE,
            STORAGE_KEYS.AUTH_TOKEN,
            STORAGE_KEYS.REFRESH_TOKEN,
            STORAGE_KEYS.USER_INFO,
            STORAGE_KEYS.CLIENT_AUTH,
            'vrs_interpreter_auth',
            'vrs_operator_notes',
            'vrs_operator_instructions',
            'vrs_held_call'
        ]);
        this.validationCache.clear();
    }
    /**
     * Quick role set for client flow (backward compatibility)
     */
    setClientRole(name) {
        const userId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const now = Date.now();
        const user = {
            id: userId,
            role: 'client',
            name: name || 'Guest',
            isAuthenticated: true,
            authenticatedAt: now,
            expiresAt: now + TOKEN_EXPIRY.CLIENT
        };
        setPersistentItem(STORAGE_KEYS.USER_ROLE, 'client');
        setPersistentItem(STORAGE_KEYS.CLIENT_AUTH, 'true');
        setPersistentItem(STORAGE_KEYS.USER_INFO, JSON.stringify(user));
        this.currentUser = user;
    }
    /**
     * Quick role set for interpreter flow (backward compatibility)
     */
    setInterpreterRole(name, languages) {
        console.warn('Interpreter shortcut auth is disabled. Use server-backed login instead.', {
            name,
            languages
        });
        this.logout();
    }
    // Private helper methods
    setUser(user, token) {
        this.currentUser = user;
        setPersistentItem(STORAGE_KEYS.USER_ROLE, user.role);
        setPersistentItem(STORAGE_KEYS.AUTH_TOKEN, JSON.stringify(token));
        setPersistentItem(STORAGE_KEYS.USER_INFO, JSON.stringify(user));
    }
    async hydrateUserFromStorage() {
        await hydratePersistentItems([
            'vrs_operator_notes',
            STORAGE_KEYS.USER_ROLE,
            STORAGE_KEYS.AUTH_TOKEN,
            STORAGE_KEYS.REFRESH_TOKEN,
            STORAGE_KEYS.USER_INFO,
            STORAGE_KEYS.CLIENT_AUTH,
            'vrs_interpreter_auth',
            'vrs_active_call',
            'vrs_held_call'
        ]);
        this.loadUserFromStorage();
    }
    loadUserFromStorage() {
        try {
            const userInfo = getPersistentItem(STORAGE_KEYS.USER_INFO);
            if (userInfo) {
                const user = JSON.parse(userInfo);
                // Check if expired
                if (user.expiresAt && Date.now() > user.expiresAt) {
                    this.logout();
                    return;
                }
                this.currentUser = user;
            }
        }
        catch (error) {
            console.error('Error loading user from storage:', error);
        }
    }
    getStoredToken() {
        try {
            const tokenStr = getPersistentItem(STORAGE_KEYS.AUTH_TOKEN);
            if (tokenStr) {
                return JSON.parse(tokenStr);
            }
        }
        catch (error) {
            console.error('Error loading token from storage:', error);
        }
        return null;
    }
    async getStoredTokenAsync() {
        try {
            const tokenStr = await getPersistentItemAsync(STORAGE_KEYS.AUTH_TOKEN);
            if (tokenStr) {
                return JSON.parse(tokenStr);
            }
        }
        catch (error) {
            console.error('Error loading token from storage:', error);
        }
        return null;
    }
    async generateLocalToken(user) {
        const header = { alg: 'HS256', typ: 'JWT' };
        const payload = {
            userId: user.id,
            role: user.role,
            iat: Math.floor(Date.now() / 1000),
            exp: user.expiresAt ? Math.floor(user.expiresAt / 1000) : undefined
        };
        const headerB64 = encodeBase64UrlJson(header);
        const payloadB64 = encodeBase64UrlJson(payload);
        const signingInput = `${headerB64}.${payloadB64}`;
        const secret = this.config.jwtSecret;
        if (!secret) {
            console.warn('[VRS Auth] No jwtSecret configured — client tokens will be unsigned.');
        }
        const signature = secret
            ? await hmacSign(signingInput, secret)
            : '';
        return `${signingInput}.${signature}`;
    }
    getAuthUrl(path) {
        const baseEndpoint = this.config.authEndpoint.replace(/\/$/, '');
        if (baseEndpoint.endsWith('/login') || baseEndpoint.endsWith('/logout') || baseEndpoint.endsWith('/validate')) {
            return baseEndpoint;
        }
        return `${baseEndpoint}/${path}`;
    }
    normalizeServerToken(serverToken, user) {
        if (typeof serverToken !== 'string') {
            return serverToken;
        }
        const issuedAt = Date.now();
        let expiresAt = issuedAt + TOKEN_EXPIRY.INTERPRETER;
        try {
            const payload = JSON.parse(atob(serverToken.split('.')[1]));
            if (payload?.exp) {
                expiresAt = payload.exp * 1000;
            }
        }
        catch (error) {
            // Fall back to default interpreter expiry if the token is opaque.
        }
        return {
            token: serverToken,
            role: user.role,
            userId: user.id,
            name: user.name,
            expiresAt,
            issuedAt
        };
    }
}
// Singleton instance
export const vrsAuthService = new VRSSAuthService();
export default vrsAuthService;
